import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { queryCache, useMutation } from "react-query";

import { Button, showNotification } from "fe-shared/src/components";
import { CloseIcon } from "fe-shared/src/components/Icons";
import {
  InvalidationModal,
  InvalidationPayload,
} from "fe-shared-requests/src/components";
import { invalidatePickupRequest } from "fe-shared-requests/src/api/pickupRequests";

import {
  REQUESTS_QUERY_KEY,
  REQUEST_ACTIVITIES_QUERY_KEY,
  REQUEST_DETAILS_QUERY_KEY,
  REQUEST_DOCUMENTS_QUERY_KEY,
} from "consts";

import styles from "./PickupRequestInvalidateButton.module.scss";

interface Props {
  requestNumber: string;
}

const PickupRequestInvalidateButton = ({ requestNumber }: Props) => {
  const { t } = useTranslation();
  const [isModalActive, setIsModalActive] = useState(false);

  const [invalidate, invalidateResult] = useMutation(
    (payload: InvalidationPayload) =>
      invalidatePickupRequest(requestNumber, payload),
    {
      onSuccess: () => {
        setIsModalActive(false);
        queryCache.invalidateQueries(REQUESTS_QUERY_KEY);
        queryCache.invalidateQueries([REQUEST_DETAILS_QUERY_KEY, requestNumber]);
        queryCache.invalidateQueries([
          REQUEST_ACTIVITIES_QUERY_KEY,
          requestNumber,
        ]);
        queryCache.invalidateQueries([
          REQUEST_DOCUMENTS_QUERY_KEY,
          requestNumber,
        ]);
        showNotification(t("pickupRequests.details.invalidateSuccess"));
      },
      onError: () => {
        queryCache.invalidateQueries([REQUEST_DETAILS_QUERY_KEY, requestNumber]);
        showNotification(
          t("pickupRequests.details.invalidateError"),
          undefined,
          CloseIcon
        );
      },
    }
  );

  return (
    <>
      <Button
        id="request-invalidate-button"
        variant="filled"
        className={styles.invalidate}
        disabled={invalidateResult.isLoading}
        onClick={() => setIsModalActive(true)}
      >
        {t("pickupRequests.details.invalidate")}
      </Button>
      <InvalidationModal
        isActive={isModalActive}
        showParticipantsOption={false}
        requestNumber={requestNumber}
        isSubmitting={invalidateResult.isLoading}
        onConfirm={invalidate}
        onCancel={() => setIsModalActive(false)}
      />
    </>
  );
};

export default PickupRequestInvalidateButton;
